import * as React from 'react';
import { useEffect, useContext, useState } from 'react';
import {
  Container,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../store/userContext';
import { SiteContext } from '../store/siteContext';
import useGetSiteName from '../utils/hooks/useGetSiteName';
import loadingUsers from './loadingUsers';

import UserHeader from './UserHeader';
import UserColumn from './UserColumn';
import SiteColumn from './SiteColumn';

const UserList: React.FC = () => {
  const {
    getUsers,
    users,
    getUsersLoading,
    editUser,
  } = useContext(UserContext);
  const { getSite } = useContext(SiteContext);
  const { getSiteName } = useGetSiteName();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const theme = useTheme();
  const { t } = useTranslation('nav');


  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const filteredUsers = (getUsersLoading ? loadingUsers : users).filter((user) =>
      user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const handleUserClick = (user: any) => {
    setSelectedUserId(user.id);
    setSelectedSiteId(null);
    user.siteIds?.forEach((siteId: number) => getSite(siteId));
  };

  const handleSiteClick = (siteId: number) => {
    setSelectedSiteId(siteId);
  };

  const handleDeleteSite = (siteId: number) => {
    const selectedUser = users.find((u) => u.id === selectedUserId);
    if (selectedUser) {
      const updatedSiteIds = selectedUser.siteIds?.filter((id) => id !== siteId) || [];
      const updatedUser: UserFormDto = {
        name: selectedUser.name,
        email: selectedUser.email,
        role: selectedUser.role,
        locale: selectedUser.locale,
        siteIds: updatedSiteIds,
      };
      editUser(selectedUser.id, updatedUser, () => {
        getUsers(true);
        setSelectedSiteId(null);
      });
    }
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);
  const filteredSiteIds = selectedUser?.siteIds?.filter((siteId) =>
      getSiteName(siteId).toLowerCase().includes(siteSearchTerm.toLowerCase())
  );

  return (
      <Container maxWidth="lg">
        <Typography
            align="left"
            variant="h3"
            sx={(theme) => ({
              ...theme.typography.listTitle,
              color: theme.palette.titleColor,
              [theme.breakpoints.down('md')]: {
                fontSize: '2.5rem',
              },
            })}
        >
          {t('t.users')}
        </Typography>
        <Grid container alignItems="stretch" sx={{ border: theme.palette.border }}>
          <UserHeader
              userSearchTerm={userSearchTerm}
              setUserSearchTerm={setUserSearchTerm}
              siteSearchTerm={siteSearchTerm}
              setSiteSearchTerm={setSiteSearchTerm}
              selectedUser={selectedUser}
          />

          <Grid container size={{ xs: 12 }} alignItems="stretch">
            <UserColumn
                filteredUsers={filteredUsers}
                selectedUserId={selectedUserId}
                onUserClick={handleUserClick}
            />

            <SiteColumn
                selectedUserId={selectedUserId}
                selectedSiteId={selectedSiteId}
                siteIds={filteredSiteIds}
                getSiteName={getSiteName}
                onSiteClick={handleSiteClick}
                onDeleteSite={handleDeleteSite}
            />
          </Grid>
        </Grid>
      </Container>
  );
};

export default UserList;
